import React from 'react';
import NumberFlow from '@number-flow/react';

import { RESPECT_REDUCED_MOTION } from '../../theme/motion';

export interface AnimatedNumberProps {
  /** The numeric value to display (and animate to on change). */
  value: number;
  /**
   * Standard `Intl.NumberFormatOptions` (e.g. `{ style: 'currency',
   * currency: 'USD' }` or `{ notation: 'compact' }`). NumberFlow formats and
   * animates each digit/part accordingly.
   */
  format?: Intl.NumberFormatOptions;
  /** Optional text rendered before the number (NumberFlow `prefix`). */
  prefix?: string;
  /** Optional text rendered after the number (NumberFlow `suffix`). */
  suffix?: string;
  /** Optional className passed through to NumberFlow. */
  className?: string;
  /**
   * Optional override for the count-up duration (milliseconds). When set, it is
   * forwarded to NumberFlow's `transformTiming` and `spinTiming`
   * (`EffectTiming`) so the digit tween lasts longer (e.g. for an intro
   * animation). When omitted, NumberFlow uses its default (fast) timing — so
   * existing callers are unaffected.
   */
  spinDurationMs?: number;
}

/** Easing matched to the ring's stroke transition so both feel cohesive. */
const SLOW_RAMP_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Animated number ticker — a thin wrapper over `@number-flow/react` with the
 * app's reduced-motion policy baked in.
 *
 * Reduced-motion branch (third-party wiring, spec §9.3): NumberFlow honors the
 * OS `prefers-reduced-motion` setting via its own `respectMotionPreference`
 * flag (default `true`). We wire that flag to the SAME global
 * `RESPECT_REDUCED_MOTION` constant — note the OPPOSITE polarity: with the
 * decoupled default (`RESPECT_REDUCED_MOTION === false`) we pass
 * `respectMotionPreference={false}`, so the ticker always animates rather than
 * snapping to the value. Flip the constant to `true` and NumberFlow falls back
 * to instant updates under OS reduce-motion, in lockstep with the rest of the
 * motion system. No call-site has to remember this.
 */
export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  format,
  prefix,
  suffix,
  className,
  spinDurationMs,
}) => {
  // Only forward timing when explicitly requested, keeping NumberFlow's fast
  // default for every other caller.
  const timing =
    spinDurationMs != null
      ? { duration: spinDurationMs, easing: SLOW_RAMP_EASING }
      : undefined;
  return (
    <NumberFlow
      value={value}
      format={format}
      prefix={prefix}
      suffix={suffix}
      className={className}
      respectMotionPreference={RESPECT_REDUCED_MOTION}
      {...(timing ? { transformTiming: timing, spinTiming: timing } : {})}
    />
  );
};

export default AnimatedNumber;
